import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DataStorageService, StepReportService } from '@pulpo/pulpo-api';
import type {
  UserRequestFilter,
  UserRequestLine,
  UserRequestStat,
  UserRequestStatScope,
} from '@pulpo/pulpo-models';
import { Subscription } from 'rxjs';
import { TextViewerDialogComponent } from '../text-viewer-dialog/text-viewer-dialog.component';
import { actionLabel, requesterDisplayName } from './user-request-stats.labels';

export interface UserRequestLinesDialogData {
  stat: UserRequestStat;
  scope: UserRequestStatScope;
  days: number;
  showPayloads: boolean;
}

/** Column id -> field the booking service sorts on (PUL-3237). */
const SORT_FIELDS: Record<string, string> = {
  date: 'date',
  action: 'action',
  status: 'status',
  product: 'product',
  dates: 'dateBegin',
  cities: 'fromIata',
  travellers: 'travellers',
  bookingId: 'bookingId',
};

/** What one requester asked for over the period, paged and sorted server side. */
@Component({
  selector: 'pulpo-user-request-lines-dialog',
  templateUrl: './user-request-lines-dialog.component.html',
  styleUrls: ['./user-request-stats.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatPaginatorModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
  ],
})
export class UserRequestLinesDialogComponent implements OnInit {
  private readonly stepReportService = inject(StepReportService);
  private readonly dataStorageService = inject(DataStorageService);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);
  private loadSubscription?: Subscription;
  readonly data = inject<UserRequestLinesDialogData>(MAT_DIALOG_DATA);

  readonly actionOptions = [
    { value: 'QuotedAvailability', label: 'List' },
    { value: 'Quote', label: 'Quote' },
    { value: 'Booking', label: 'Book' },
  ];
  readonly pageSize = 20;
  readonly title = requesterDisplayName(this.data.stat);
  readonly actionLabel = actionLabel;

  lines: UserRequestLine[] = [];
  totalLines = 0;
  pageIndex = 0;
  actionFilter?: string;
  statusFilter?: string;
  sort: Sort = { active: 'date', direction: 'desc' };
  loading = false;

  get columns(): string[] {
    const columns = [
      'date',
      'action',
      'status',
      'product',
      'dates',
      'cities',
      'travellers',
      'bookingId',
      'time',
    ];
    return this.data.showPayloads ? [...columns, 'payloads'] : columns;
  }

  constructor() {
    inject(DestroyRef).onDestroy(() => this.loadSubscription?.unsubscribe());
  }

  ngOnInit(): void {
    this.load();
  }

  onFilterChange(): void {
    this.pageIndex = 0;
    this.load();
  }

  onSortChange(sort: Sort): void {
    this.sort = sort;
    this.pageIndex = 0;
    this.load();
  }

  onPage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.load();
  }

  showPayload(fileName: string): void {
    this.dataStorageService
      .getStepReportFileByName(fileName)
      .subscribe((res) => {
        let content = res.body || '';
        if (fileName.endsWith('.json') && content) {
          try {
            content = JSON.stringify(JSON.parse(content), null, 2);
          } catch {
            // not valid json: shown as received
          }
        }
        this.dialog.open(TextViewerDialogComponent, {
          data: {
            content,
            language: fileName.endsWith('.xml') ? 'xml' : 'json',
          },
          width: '75%',
          height: '90%',
        });
      });
  }

  private load(): void {
    const { stat, scope, days } = this.data;
    const filter: UserRequestFilter = {
      userId: stat.userId,
      requester: stat.userId ? undefined : stat.requester,
      days,
      action: this.actionFilter,
      status: this.statusFilter,
      // no direction means back to the default order, latest first
      sort: this.sort.direction ? SORT_FIELDS[this.sort.active] : undefined,
      direction: this.sort.direction || undefined,
    };
    this.loading = true;
    // a slower previous page, filter or sort must not overwrite this one
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.stepReportService
      .getUserRequests(scope, filter, this.pageIndex, this.pageSize)
      .subscribe({
        next: (page) => {
          this.lines = page.content;
          this.totalLines = page.totalElements;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.lines = [];
          this.totalLines = 0;
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }
}
